import { Component } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { SubscriptSizing } from '@angular/material/form-field';
import { NFormRecordRef } from '@pebula/nform';
import { SgAutoCompleteSelectQueryChange } from '@sg/ui-kit/auto-complete-select';
import { SgAutoCompleteSelect2GroupBy, SgAutoCompleteSelect2QueryChange } from '@sg/ui-kit/core';

declare module '@pebula/nform/lib/types/form-element-type' {
  interface FormElementType {
    checkbox: {};
    fileUpload: {
      multiple?: boolean;
      matIcon?: string;
      accept?: string;
    };
    selectAutoComplete: {
      onQueryChange?: (event: SgAutoCompleteSelectQueryChange, ri: NFormRecordRef<'selectAutoComplete'>) => void;
      scrollEnd?: (ri: NFormRecordRef<'selectAutoComplete'>) => void;
      multiple?: true;
      options: Array<{
        value: any;
        label?: string;
        optGroup?: boolean;
      }>;
      groupBy: (option: MatOption) => string;
      searchInputPlaceholder: string;
      subscriptSizing: SubscriptSizing;
      size: 'small' | 'default';
    };
    selectAutoComplete2: {
      onQueryChange?: (event: SgAutoCompleteSelect2QueryChange, ri: NFormRecordRef<'selectAutoComplete2'>) => void;
      scrollEnd?: (ri: NFormRecordRef<'selectAutoComplete2'>) => void;
      multiple?: boolean;
      options: Array<{
        value: any;
        label?: string;
      }>;
      groupBy?: SgAutoCompleteSelect2GroupBy;
      searchInputPlaceholder?: string;
      subscriptSizing?: 'fixed' | 'dynamic';
      size?: 'small' | 'default';
      variant?: 'default' | 'secondary' | 'underline';
      labelKey?: string;
      valueKey?: string;
      multiSelectDisplay?: 'count' | 'chips';
      panelWidth?: 'trigger' | 'auto';
      hideInput?: boolean;
      clearable?: boolean;
    };
  }
}

const ORIGINAL_OPTIONS = Symbol();

@Component({
  selector: 'sg-common-form-types',
  templateUrl: './common-form-types.component.html',
  styleUrls: ['./common-form-types.component.scss'],
  standalone: false,
})
export class SgCommonFormTypesComponent {
  autoSelectValueTransform = (value) => value.value;

  fileUploadClick(event: MouseEvent, logicalInput: HTMLInputElement, control: FormControl): void {
    if (!control.disabled) {
      logicalInput.dispatchEvent(new MouseEvent(event.type, event));
    }
  }
  fileUploadClear(viewInput: HTMLInputElement, logicalInput: HTMLInputElement, control: FormControl): void {
    viewInput.value = '';
    logicalInput.value = '';
    logicalInput.files = undefined;
    control.setValue(null);
  }
  fileUploadChange(viewInput: HTMLInputElement, logicalInput: HTMLInputElement, control: FormControl): void {
    viewInput.value = '';
    if (logicalInput.files) {
      viewInput.value = Array.from(logicalInput.files)
        .map((f) => `${f.name} (${f.size})`)
        .join(', ');
    }
    control.setValue(logicalInput.files);
  }

  autoSelectSearchQueryChange(
    event: SgAutoCompleteSelectQueryChange,
    nformRecord: NFormRecordRef<'selectAutoComplete'>
  ) {
    if (typeof nformRecord.data.onQueryChange === 'function') {
      nformRecord.data.onQueryChange(event, nformRecord);
    } else {
      let options = nformRecord.data.options;

      if (!options) {
        options = [];
      } else {
        const { query } = event;
        const rootOptions = nformRecord.data[ORIGINAL_OPTIONS] || options;
        nformRecord.data[ORIGINAL_OPTIONS] = rootOptions;
        if (!query) {
          options = rootOptions.slice();
          nformRecord.data[ORIGINAL_OPTIONS] = undefined;
        } else {
          const filterValue = query.toLowerCase();
          options = rootOptions.filter((rootOption) => {
            if (
              nformRecord.data.groupBy &&
              nformRecord.data.groupBy(rootOption)?.toLowerCase().indexOf(filterValue) > -1
            ) {
              return true;
            }
            return (rootOption.label || rootOption.value).toLowerCase().indexOf(filterValue) > -1;
          });
        }
      }
      nformRecord.mergeData({ options });
    }
  }

  autoSelectScrollEnd(nformRecord: NFormRecordRef<'selectAutoComplete'>) {
    if (typeof nformRecord.data.scrollEnd === 'function') {
      nformRecord.data.scrollEnd(nformRecord);
    }
  }

  autoSelectOpenChange(isOpen: boolean, formFieldData: NFormRecordRef<'selectAutoComplete'>['data']): void {
    if (!isOpen && formFieldData[ORIGINAL_OPTIONS]) {
      formFieldData.options = formFieldData[ORIGINAL_OPTIONS].slice();
      formFieldData[ORIGINAL_OPTIONS] = undefined;
    }
  }

  autoSelect2SearchQueryChange(
    event: SgAutoCompleteSelect2QueryChange,
    nformRecord: NFormRecordRef<'selectAutoComplete2'>
  ) {
    if (typeof nformRecord.data.onQueryChange === 'function') {
      nformRecord.data.onQueryChange(event, nformRecord);
    } else {
      let options = nformRecord.data.options;

      if (!options) {
        options = [];
      } else {
        const { query } = event;
        const rootOptions = nformRecord.data[ORIGINAL_OPTIONS] || options;
        nformRecord.data[ORIGINAL_OPTIONS] = rootOptions;
        if (!query) {
          options = rootOptions.slice();
          nformRecord.data[ORIGINAL_OPTIONS] = undefined;
        } else {
          const filterValue = query.toLowerCase();
          options = rootOptions.filter((rootOption) => {
            if (
              nformRecord.data.groupBy &&
              nformRecord.data.groupBy(rootOption)?.toLowerCase().indexOf(filterValue) > -1
            ) {
              return true;
            }

            return (rootOption.label || rootOption.value).toLowerCase().indexOf(filterValue) > -1;
          });
        }
      }
      nformRecord.mergeData({ options });
    }
  }

  autoSelect2ScrollEnd(nformRecord: NFormRecordRef<'selectAutoComplete2'>) {
    if (typeof nformRecord.data.scrollEnd === 'function') {
      nformRecord.data.scrollEnd(nformRecord);
    }
  }

  autoSelect2OpenChange(isOpen: boolean, formFieldData: NFormRecordRef<'selectAutoComplete2'>['data']): void {
    if (!isOpen && formFieldData[ORIGINAL_OPTIONS]) {
      formFieldData.options = formFieldData[ORIGINAL_OPTIONS].slice();
      formFieldData[ORIGINAL_OPTIONS] = undefined;
    }
  }
}
